import { Injectable } from '@angular/core';

import {
  AssistantFile,
  AssistantMessage,
  AssistantTab
} from '@core/models/assistant/assistant.model';

const PREFIX = 'gfe.assistant.conv.';
const LEGACY_PREFIX = 'gfe.assistant.conversations.';
const VERSION = 2;
const MAX_CONVERSATIONS = 10;
const MAX_AGE_MS = 7 * 24 * 60 * 60_000;
/** A sender note older than this belongs to a tab that closed or crashed mid-reply. */
export const SENDER_TTL_MS = 10_000;

export interface Sender {
  pageId: string;
  renewedAt: number;
}

type SavedTab = Omit<AssistantTab, 'streaming'>;

export interface StoredConversation {
  tab: AssistantTab;
  openedAt: number;
  updatedAt: number;
  sender?: Sender;
}

export type ScopeChange =
  | { kind: 'upsert'; conversation: StoredConversation }
  | { kind: 'delete'; id: string }
  | { kind: 'reload' };

interface ConversationRecord {
  version: number;
  openedAt: number;
  updatedAt: number;
  tab: SavedTab;
  sender?: Sender;
}

interface DeletionMarker {
  version: number;
  deletedAt: number;
}

interface LegacyList {
  version: number;
  savedAt: number;
  activeId: string;
  tabs: SavedTab[];
}

export function isLive(sender: Sender | undefined, now = Date.now()): boolean {
  return !!sender && now - sender.renewedAt < SENDER_TTL_MS;
}

// A reply still streaming with nobody sending it was cancelled by the server when its connection dropped.
export function interruptStreaming(tab: AssistantTab): AssistantTab {
  return tab.messages.some((m) => m.status === 'streaming')
    ? { ...tab, messages: tab.messages.map(interrupted) }
    : tab;
}

/**
 * Keeps each user's assistant conversations per data space in localStorage, one record per
 * conversation so that browser tabs writing different conversations never overwrite each other.
 * Only the transcript is kept: the model's history stays on the assistant-server, which answers
 * `conversation_not_found` once it has lost it.
 */
@Injectable({ providedIn: 'root' })
export class AssistantConversationStore {
  load(
    userName: string,
    dataSpaceId: number
  ): { conversations: StoredConversation[]; legacyActiveId?: string } {
    this.dropExpired();
    const legacyActiveId = this.migrate(userName, dataSpaceId);
    const conversations: StoredConversation[] = [];
    for (const [key, raw] of entries(scopePrefix(userName, dataSpaceId))) {
      const parsed = parse(raw);
      if (parsed === null || parsed === undefined) {
        if (parsed === null) remove(key);
        continue;
      }
      if (isMarker(parsed)) continue;
      conversations.push(toStored(parsed));
    }
    conversations.sort((a, b) => a.openedAt - b.openedAt);
    return legacyActiveId === undefined
      ? { conversations }
      : { conversations, legacyActiveId };
  }

  read(userName: string, dataSpaceId: number, id: string): StoredConversation | null {
    const parsed = parse(read(keyOf(userName, dataSpaceId, id)));
    return parsed && !isMarker(parsed) ? toStored(parsed) : null;
  }

  /** Returns the files whose content it dropped from this data space's conversations to make room. */
  write(
    userName: string,
    dataSpaceId: number,
    tab: AssistantTab,
    sender?: Sender
  ): string[] {
    const key = keyOf(userName, dataSpaceId, tab.id);
    if (tab.messages.length === 0) return [];
    const existing = parse(read(key));
    const isNew = !existing || isMarker(existing);
    const now = Date.now();
    const { streaming: _streaming, ...kept } = tab;
    const record: ConversationRecord = {
      version: VERSION,
      openedAt: isNew ? now : existing.openedAt,
      updatedAt: now,
      tab: kept,
      ...(sender ? { sender } : {})
    };
    const dropped: string[] = [];
    const dropFile = () => {
      const fileId = this.dropOldestFile(key, record);
      if (fileId !== null) dropped.push(fileId);
      return fileId !== null;
    };
    // Only a new record can push the scope past the cap; rewrites during a reply skip the scan.
    if (this.put(key, () => JSON.stringify(record), dropFile) && isNew)
      this.enforceCap(scopePrefix(userName, dataSpaceId));
    return dropped;
  }

  markDeleted(userName: string, dataSpaceId: number, id: string): void {
    const marker: DeletionMarker = { version: VERSION, deletedAt: Date.now() };
    this.put(keyOf(userName, dataSpaceId, id), () => JSON.stringify(marker));
  }

  change(
    userName: string,
    dataSpaceId: number,
    key: string | null,
    newValue: string | null
  ): ScopeChange | null {
    if (key === null) return { kind: 'reload' };
    const prefix = scopePrefix(userName, dataSpaceId);
    if (!key.startsWith(prefix)) return null;
    const id = key.slice(prefix.length);
    if (!id || id.includes('.')) return null;
    if (newValue === null) return { kind: 'delete', id };
    const parsed = parse(newValue);
    if (!parsed) return null;
    return isMarker(parsed)
      ? { kind: 'delete', id }
      : { kind: 'upsert', conversation: toStored(parsed) };
  }

  // Out of space: other data spaces' records go first, oldest first, then this one's file contents, which
  // are the largest and the easiest to regenerate, then this one's oldest records. Any other failure
  // (storage disabled, a privacy mode) cannot be fixed by evicting, which would only drop file contents.
  private put(key: string, value: () => string, dropFile = () => false): boolean {
    let serialised = value();
    for (;;) {
      const result = tryPut(key, serialised);
      if (result !== 'full') return result === 'saved';
      if (this.dropOldestExcept(key, false)) continue;
      if (dropFile()) {
        serialised = value();
        continue;
      }
      if (!this.dropOldestExcept(key, true)) return false;
    }
  }

  private dropOldestExcept(keep: string, sameScope: boolean): boolean {
    const scope = scopeOf(keep);
    const oldest = [...entries(PREFIX), ...entries(LEGACY_PREFIX)]
      .filter(([key]) => key !== keep && key.startsWith(scope) === sameScope)
      .map(([key, raw]) => ({ key, stamp: stampOf(raw) }))
      .sort((a, b) => a.stamp - b.stamp)[0];
    if (!oldest) return false;
    remove(oldest.key);
    return true;
  }

  /**
   * Drops the content of the oldest file among `pending`, about to be written to `key`, and this data
   * space's stored conversations, keeping each record's `updatedAt` so it keeps its place for removal.
   * A conversation being replied in is skipped: its sender would write the content straight back.
   */
  private dropOldestFile(key: string, pending: ConversationRecord): string | null {
    const stored = entries(scopeOf(key)).flatMap(([k, raw]) => {
      const parsed = k === key ? null : parse(raw);
      return parsed && !isMarker(parsed) && !isLive(parsed.sender)
        ? [{ key: k, record: parsed }]
        : [];
    });
    const oldest = [{ key, record: pending }, ...stored]
      .flatMap((c) => filesOf(c.record.tab).map((file) => ({ ...c, file })))
      .sort((a, b) => a.file.createdAt - b.file.createdAt)[0];
    if (!oldest) return null;
    const { record, file } = oldest;
    [record.tab] = withoutFileContents([record.tab], [file.fileId]);
    if (record !== pending && tryPut(oldest.key, JSON.stringify(record)) !== 'saved')
      return null;
    return file.fileId;
  }

  private enforceCap(prefix: string): void {
    const records = entries(prefix).flatMap(([key, raw]) => {
      const parsed = parse(raw);
      return parsed && !isMarker(parsed) ? [{ key, record: parsed }] : [];
    });
    const removable = records
      .filter(({ record }) => !isLive(record.sender))
      .sort((a, b) => a.record.updatedAt - b.record.updatedAt);
    for (
      let excess = records.length - MAX_CONVERSATIONS;
      excess > 0 && removable.length;
      excess--
    )
      remove(removable.shift()!.key);
  }

  private dropExpired(): void {
    const now = Date.now();
    for (const [key, raw] of [...entries(PREFIX), ...entries(LEGACY_PREFIX)])
      if (now - stampOf(raw) > MAX_AGE_MS) remove(key);
  }

  private migrate(userName: string, dataSpaceId: number): string | undefined {
    const key = `${LEGACY_PREFIX}${encodeURIComponent(userName)}.${dataSpaceId}`;
    const raw = read(key);
    if (raw === null) return undefined;
    remove(key);
    const legacy = parseLegacy(raw);
    if (!legacy) return undefined;
    const tabs = legacy.tabs.filter((t) => t.messages.length > 0).slice(-MAX_CONVERSATIONS);
    tabs.forEach((tab, i) => {
      const record: ConversationRecord = {
        version: VERSION,
        openedAt: legacy.savedAt - tabs.length + i,
        updatedAt: legacy.savedAt,
        tab
      };
      this.put(keyOf(userName, dataSpaceId, tab.id), () => JSON.stringify(record));
    });
    return legacy.activeId;
  }
}

export function userPart(userName: string): string {
  return encodeURIComponent(userName).replace(/\./g, '%2E');
}

function scopePrefix(userName: string, dataSpaceId: number): string {
  return `${PREFIX}${userPart(userName)}.${dataSpaceId}.`;
}

function scopeOf(key: string): string {
  return key.slice(0, key.lastIndexOf('.') + 1);
}

function keyOf(userName: string, dataSpaceId: number, id: string): string {
  return scopePrefix(userName, dataSpaceId) + id;
}

function toStored(record: ConversationRecord): StoredConversation {
  const tab: AssistantTab = { ...record.tab, streaming: false };
  return {
    tab: isLive(record.sender) ? tab : interruptStreaming(tab),
    openedAt: record.openedAt,
    updatedAt: record.updatedAt,
    ...(record.sender ? { sender: record.sender } : {})
  };
}

function interrupted(m: AssistantMessage): AssistantMessage {
  return m.status !== 'streaming'
    ? m
    : {
        ...m,
        status: 'cancelled',
        tools: m.tools.map((t) =>
          t.status === 'running' ? { ...t, status: 'cancelled' } : t
        )
      };
}

function filesOf(tab: SavedTab): AssistantFile[] {
  return tab.messages.flatMap((m) => m.files ?? []).filter((f) => f.content !== null);
}

// Untouched tabs keep their identity: the sync rewrites every conversation whose object changed, which
// would bump each one's `updatedAt` and reorder them for removal.
export function withoutFileContents<T extends SavedTab>(tabs: T[], fileIds: string[]): T[] {
  const holds = (m: AssistantMessage) => !!m.files?.some((f) => fileIds.includes(f.fileId));
  return tabs.map((t) =>
    t.messages.some(holds)
      ? {
          ...t,
          messages: t.messages.map((m) =>
            holds(m)
              ? {
                  ...m,
                  files: m.files!.map((f) =>
                    fileIds.includes(f.fileId) ? { ...f, content: null } : f
                  )
                }
              : m
          )
        }
      : t
  );
}

function isMarker(value: ConversationRecord | DeletionMarker): value is DeletionMarker {
  return 'deletedAt' in value;
}

/** `undefined` for an absent key, `null` for one that cannot be read. */
function parse(raw: string | null): ConversationRecord | DeletionMarker | null | undefined {
  if (raw === null) return undefined;
  try {
    const value = JSON.parse(raw) as ConversationRecord & DeletionMarker;
    if (value?.version !== VERSION) return null;
    if (typeof value.deletedAt === 'number')
      return { version: VERSION, deletedAt: value.deletedAt };
    const valid =
      typeof value.openedAt === 'number' &&
      typeof value.updatedAt === 'number' &&
      typeof value.tab?.id === 'string' &&
      Array.isArray(value.tab.messages);
    return valid ? value : null;
  } catch {
    return null;
  }
}

function parseLegacy(raw: string): LegacyList | null {
  try {
    const value = JSON.parse(raw) as LegacyList;
    const valid =
      value?.version === 1 &&
      typeof value.savedAt === 'number' &&
      Array.isArray(value.tabs) &&
      value.tabs.every((t) => typeof t?.id === 'string' && Array.isArray(t.messages));
    return valid ? value : null;
  } catch {
    return null;
  }
}

// An unreadable record counts as the oldest.
function stampOf(raw: string): number {
  try {
    const value = JSON.parse(raw) as Partial<
      ConversationRecord & DeletionMarker & LegacyList
    >;
    return Number(value?.updatedAt ?? value?.deletedAt ?? value?.savedAt) || 0;
  } catch {
    return 0;
  }
}

function entries(prefix: string): [string, string][] {
  const found: [string, string][] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key?.startsWith(prefix)) continue;
      const raw = read(key);
      if (raw !== null) found.push([key, raw]);
    }
  } catch {
    // Storage unavailable: nothing to list.
  }
  return found;
}

function tryPut(key: string, value: string): 'saved' | 'full' | 'failed' {
  try {
    localStorage.setItem(key, value);
    return 'saved';
  } catch (err) {
    return isQuotaExceeded(err) ? 'full' : 'failed';
  }
}

// Older Firefox versions name it NS_ERROR_DOM_QUOTA_REACHED.
function isQuotaExceeded(err: unknown): boolean {
  return (
    err instanceof DOMException &&
    (err.name === 'QuotaExceededError' || err.name === 'NS_ERROR_DOM_QUOTA_REACHED')
  );
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function remove(key: string): void {
  try {
    localStorage.removeItem(key);
  } catch {
    // Storage unavailable: nothing was saved there either.
  }
}
